import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Callout } from 'seagull';
import { AnchorLogo } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext, useStore } from '~/contexts';
import { useLocationWithState } from '~/hooks/useLocationWithState';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { FormTitle } from '../components/FormComponents';
import UnauthorizedPageContainer from '../components/UnauthorizedPageContainer';
import styles from './LoginScreen.module.scss';
import { LoginFlow } from './LoginScreen.types';
import { getIsSimplifiedLogin, getLoginScreenTexts, getTheNextStep } from './LoginScreen.utils';
import EmailStep from './Steps/Email';
import OTPForm from './Steps/OTPForm';
import PasswordStep from './Steps/Password';
export function getClientViewURL(pathname) {
    return "".concat(pathname, "?view=client");
}
export const GetLoginScreenTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            LoginScreenTID: {
                Root: 'login-form',
                ServerError: 'login-server-error',
                SessionTimeoutError: 'session-timeout-error'
            }
        }
    });
export function LoginScreen() {
    const location = useLocationWithState();
    const { t: loginTexts } = useLocaleKeys('auth.screens');
    const { LoginScreenTID } = useMemo(()=>GetLoginScreenTID(), []);
    const locationState = location.state;
    const authError = useStore((state)=>state.authError);
    const [currentStep, setCurrentStep] = useState({
        step: LoginFlow.Email
    });
    const { cookies } = useIoContext();
    const hasVendorAccount = cookies.getHasVendorAccountFromUserDetailsCookie();
    const isSimplifiedLogin = getIsSimplifiedLogin({
        setLoginFlow: locationState === null || locationState === void 0 ? void 0 : locationState.setLoginFlow,
        locationSearch: location.search,
        hasVendorAccount: hasVendorAccount !== null && hasVendorAccount !== void 0 ? hasVendorAccount : undefined
    });
    const { welcomeText, emailPromptText, isAccessProposalTitle, welcomePlacement } = getLoginScreenTexts({
        isSimplifiedLogin,
        isAccessProposalLogin: !!(locationState === null || locationState === void 0 ? void 0 : locationState.accessProposal),
        accessProposalTitle: loginTexts.accessProposal.title(),
        accessProposalEmailPrompt: loginTexts.accessProposal.enterEmailToAccess(),
        welcomeToAnchor: loginTexts.login.welcomeToAnchor(),
        enterEmailToAccessAccount: loginTexts.email.enterEmailToAccess()
    });
    useSetDocumentTitle(loginTexts.login.documentTitle());
    const welcomeTitle = /*#__PURE__*/ _jsx(FormTitle, {
        testId: isAccessProposalTitle ? LoginScreenTID.AccessTitle() : undefined,
        children: welcomeText
    });
    const titleMP = welcomePlacement === 'title' ? welcomeTitle : /*#__PURE__*/ _jsx(FormTitle, {
        children: /*#__PURE__*/ _jsx(AnchorLogo, {
            className: styles.logo
        })
    });
    function getEmailStepSubtitle() {
        if (authError != null) {
            return /*#__PURE__*/ _jsx(Callout, {
                hideIcon: true,
                intent: "error",
                testId: LoginScreenTID.ServerError(),
                children: loginTexts.login.serverError()
            });
        }
        if (welcomePlacement === 'emailSubtitle') {
            return welcomeTitle;
        }
        if (locationState === null || locationState === void 0 ? void 0 : locationState.sessionTimeout) {
            return /*#__PURE__*/ _jsx(Callout, {
                hideIcon: true,
                intent: "error",
                testId: LoginScreenTID.SessionTimeoutError(),
                children: loginTexts.login.sessionTimeout()
            });
        }
        return null;
    }
    return /*#__PURE__*/ _jsxs(UnauthorizedPageContainer, {
        animatedBackground: !isSimplifiedLogin,
        hideBanner: isSimplifiedLogin,
        testId: LoginScreenTID.Root(),
        otpLayout: currentStep.step === LoginFlow.OTP || currentStep.step === LoginFlow.EmailVerification,
        children: [
            currentStep.step === LoginFlow.Email && /*#__PURE__*/ _jsx(EmailStep, {
                onSubmit: (param)=>{
                    let { email, flow } = param;
                    setCurrentStep({
                        step: getTheNextStep({
                            flow,
                            isSimplifiedLogin
                        }),
                        email
                    });
                },
                titleMP: titleMP,
                subtitleMP: getEmailStepSubtitle(),
                emailPrompt: emailPromptText,
                simplified: isSimplifiedLogin,
                emailFromState: locationState === null || locationState === void 0 ? void 0 : locationState.recipientEmail
            }),
            currentStep.step === LoginFlow.Password && /*#__PURE__*/ _jsx(PasswordStep, {
                email: currentStep.email,
                onClickRequestOTP: ()=>{
                    setCurrentStep({
                        step: LoginFlow.OTP,
                        email: currentStep.email
                    });
                },
                titleMP: titleMP,
                subtitleMP: isSimplifiedLogin ? /*#__PURE__*/ _jsx(FormTitle, {
                    children: loginTexts.login.loginToContinue()
                }) : null
            }),
            currentStep.step === LoginFlow.OTP && /*#__PURE__*/ _jsx(OTPForm, {
                email: currentStep.email,
                flow: LoginFlow.OTP,
                onBack: ()=>setCurrentStep({
                        step: LoginFlow.Email
                    }),
                shouldRequestOTP: true
            }),
            currentStep.step === LoginFlow.EmailVerification && /*#__PURE__*/ _jsx(OTPForm, {
                email: currentStep.email,
                flow: LoginFlow.EmailVerification,
                onBack: ()=>setCurrentStep({
                        step: LoginFlow.Email
                    }),
                shouldRequestOTP: true
            })
        ]
    });
}
